import React from 'react';
import { Link } from 'react-router-dom';
import { STAFF } from '../data/staff';
import { CONTACT_EMAIL, mailto } from '../lib/site';

const CORE_VALUES = [
  { title: 'Innovation', desc: 'Pushing boundaries and exploring new possibilities in technology and business solutions.' },
  { title: 'Integrity', desc: 'Upholding high ethical standards in all our interactions and business practices.' },
  { title: 'Collaboration', desc: 'Partnerships and teamwork to achieve more than individual efforts.' },
  { title: 'Excellence', desc: 'Striving for the highest quality in every project, service, and interaction.' },
  { title: 'Entertainment', desc: 'Creating engaging and enjoyable experiences that captivate and inspire.' },
  { title: 'Equal Opportunities', desc: 'Fair access to opportunities and resources for all stakeholders and communities.' },
  { title: 'Education', desc: 'Continuous learning and knowledge sharing across our organization.' },
  { title: 'Sustainability', desc: 'Building solutions that are environmentally conscious and socially responsible.' },
];

const textLink =
  'text-primary-300 hover:text-primary-200 underline underline-offset-4 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400';
const frame = 'rounded-2xl border border-secondary-700/60 bg-secondary-900';
const anchor = 'scroll-mt-[calc(var(--header-height,3.5rem)+0.75rem)]';

const About: React.FC = () => (
  <div style={{ background: 'radial-gradient(ellipse 80% 40% at 50% 0%, rgba(217,70,239,0.05) 0%, transparent 60%)' }}>
    <section className="page-shell page-section-major">
      <div className="page-inner">
        <header className="mb-6 sm:mb-8">
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-2">
            <span className="cw">About </span>
            <span className="cb">FairCorp</span>
          </h1>
          <p className="text-secondary-400">Who we are, what we are aiming for, and what we value.</p>
          <nav aria-label="On this page" className="flex flex-wrap gap-x-4 gap-y-1 mt-4 text-sm font-semibold">
            <a href="#story" className={textLink}>Our story</a>
            <a href="#mission" className={textLink}>Mission and vision</a>
            <a href="#values" className={textLink}>Core values</a>
            <a href="#company" className={textLink}>How FairCorp is set up</a>
            <a href="#team" className={textLink}>Our team</a>
          </nav>
        </header>

        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <section id="story" className={`${frame} ${anchor} p-5`} aria-labelledby="about-story">
            <h2 id="about-story" className="text-lg font-bold text-white mb-2">Our story</h2>
            <p className="text-secondary-400 text-sm mb-3">
              The founders were looking to build the perfect game for themselves, then learned how much more they could do with their skills. Building helpful apps for all, hosting and building sites for anybody, and keeping things affordable for everyone fit their personal ethics better.
            </p>
            <p className="text-secondary-400 text-sm mb-3">
              They are not trying to become a large billion-dollar corporation that makes endless sums. Everything they make is either reinvested into infrastructure or openly donated, aside from basic living necessities and costs.
            </p>
            <p className="text-secondary-400 text-sm">
              Today, we work to create products and solutions at low expense to the consumer, while maintaining our core values of innovation, integrity, and collaboration.
            </p>
          </section>
          <section id="mission" className={`${frame} ${anchor} p-5`} aria-labelledby="about-mission">
            <h2 id="about-mission" className="text-lg font-bold text-white mb-2">Mission &amp; vision</h2>
            <h3 className="text-sm font-semibold text-primary-300">Mission</h3>
            <p className="text-secondary-400 text-sm mb-3">
              To empower businesses through innovative technology solutions while fostering a culture of continuous learning and sustainable growth.
            </p>
            <h3 className="text-sm font-semibold text-primary-300">Vision</h3>
            <p className="text-secondary-400 text-sm">
              To be the leading force in innovation of advanced technology, making cutting-edge solutions accessible and affordable to organizations and individuals worldwide.
            </p>
          </section>
        </div>

        <section id="values" aria-labelledby="about-values" className={`${anchor} mb-8`}>
          <h2 id="about-values" className="text-lg font-bold text-white mb-3">Core values</h2>
          <dl className={`${frame} grid sm:grid-cols-2 gap-x-8 gap-y-3 p-5`}>
            {CORE_VALUES.map((v) => (
              <div key={v.title}>
                <dt className="font-semibold text-primary-200 text-sm">{v.title}</dt>
                <dd className="text-secondary-400 text-sm">{v.desc}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section id="company" aria-labelledby="about-company" className={`${frame} ${anchor} p-5 mb-8`}>
          <h2 id="about-company" className="text-lg font-bold text-white mb-2">How FairCorp is set up</h2>
          <p className="text-secondary-300 text-sm mb-3">
            Founded in 2024 and based in Broomfield, CO. FairCorp is a trade name. It is not a separate legal entity. Brennan Faires and Andrew Fairbank are the names the trade name stands in for.
          </p>
          <p className="text-sm">
            <Link to="/tos" className={textLink}>Read the terms</Link>
          </p>
        </section>

        <section id="team" aria-labelledby="about-team" className={`${anchor} mb-8`}>
          <h2 id="about-team" className="text-lg font-bold text-white mb-3">Our team</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {STAFF.map((member) => (
              <article key={member.id} className={`${frame} p-5`}>
                <div className="flex items-center gap-4 mb-4">
                  {member.image ? (
                    <img src={member.image} alt={member.name} className="w-16 h-16 rounded-xl object-cover border border-secondary-700/60" />
                  ) : (
                    <div className="w-16 h-16 rounded-xl border border-secondary-700/60 bg-primary-700 flex items-center justify-center" aria-hidden="true">
                      <span className="text-lg font-bold text-primary-200">
                        {member.name.split(' ').map((part) => part[0]).join('')}
                      </span>
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-bold text-white">{member.name}</h3>
                    <p className="text-sm text-primary-300">{member.role}</p>
                  </div>
                </div>
                <dl className="space-y-3 text-sm">
                  <div>
                    <dt className="font-semibold text-primary-200">About</dt>
                    <dd className="text-secondary-300 mt-1">{member.bio}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold text-primary-200">Experience</dt>
                    <dd className="text-secondary-300 mt-1">{member.priorExperience}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold text-primary-200">Education</dt>
                    <dd className="text-secondary-300 mt-1">{member.education}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold text-primary-200">Development Goal</dt>
                    <dd className="text-secondary-300 mt-1">{member.biggestGoal}</dd>
                  </div>
                </dl>
                {(member.linkedin || member.email) && (
                  <p className="text-sm mt-4 flex flex-wrap gap-x-4 gap-y-1">
                    {member.linkedin && (
                      <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className={textLink}>LinkedIn</a>
                    )}
                    {member.email && (
                      <a href={mailto()} className={`break-all ${textLink}`}>{CONTACT_EMAIL}</a>
                    )}
                  </p>
                )}
              </article>
            ))}
          </div>
        </section>

        <p className="text-sm text-secondary-400">
          See what we do on <Link to="/services" className={textLink}>Services</Link> and <Link to="/work" className={textLink}>Our Work</Link>,
          or <Link to="/contact" className={textLink}>get in touch</Link>.
        </p>
      </div>
    </section>
  </div>
);

export default About;
